import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import type { FiskilSyncStatusResponse } from "./types";

const allImportResourcesSaved = (status: FiskilSyncStatusResponse): boolean =>
  status.accounts_saved === true &&
  status.balances_saved === true &&
  status.transactions_saved === true;

/** Backend reports vault import is finished (Phase 2 fields when present). */
export const isFiskilImportAlreadyPersisted = (status: FiskilSyncStatusResponse): boolean => {
  if (status.complete === true) {
    return true;
  }
  if (allImportResourcesSaved(status)) {
    return true;
  }
  return status.has_unsaved_synced_data === false;
};

/**
 * Whether to run recovery import on manage-accounts load.
 * Phase 1: has_unsaved_synced_data. Phase 2: also missing_accounts when API provides them.
 */
export const shouldRecoverFiskilImport = (status: FiskilSyncStatusResponse): boolean => {
  if (isFiskilImportAlreadyPersisted(status)) {
    return false;
  }

  const missingCount = status.missing_accounts?.length ?? 0;
  if (missingCount > 0) {
    return true;
  }

  return status.has_unsaved_synced_data === true;
};

export const fiskilRecoveryToastKey = (status: FiskilSyncStatusResponse): string => {
  const missingCount = status.missing_accounts?.length ?? 0;
  if (missingCount > 0) {
    return "fiskilRecoverMissingAccounts";
  }
  return "fiskilRecoverPendingImport";
};

export type FiskilBootstrapAction = "recovery" | "sync" | "skip";

type VaultFiskilAccountRef = { id?: string; basiqAccountId?: string | null };

type VaultTransactionRef = {
  accountLink?: { id?: string | null };
  definition?: string;
};

/** Vault Fiskil account with no imported (non-pending) transactions — partial import backfill. */
export const fiskilVaultAccountsMissingImportedTransactions = (
  vaultFiskilAccounts: VaultFiskilAccountRef[],
  transactions: VaultTransactionRef[],
): boolean => {
  return vaultFiskilAccounts.some((account) => {
    if (!account.id) {
      return false;
    }
    const hasImported = transactions.some(
      (t) => t.accountLink?.id === account.id && t.definition === TransactionStatusEnum.transaction,
    );
    return !hasImported;
  });
};

/** Fiskil API account_ids that are not represented in the vault. */
export const fiskilApiIdsMissingFromVault = (
  apiAccountIds: string[],
  vaultFiskilAccounts: VaultFiskilAccountRef[],
): string[] => {
  const vaultIds = vaultFiskilAccounts.reduce<Set<string>>((acc, ref) => {
    const id = ref.basiqAccountId;
    if (typeof id === "string" && id.length > 0) {
      acc.add(id);
    }
    return acc;
  }, new Set());
  return apiAccountIds.filter((id) => !vaultIds.has(id));
};

/**
 * Bootstrap / session recovery: full import when backend or vault is out of sync with Fiskil;
 * incremental sync when vault already has all API accounts.
 */
export type ResolveFiskilBootstrapOptions = {
  /** When true, at least one vault Fiskil account has no imported transactions. */
  vaultAccountsMissingTransactions?: boolean;
};

export const resolveFiskilBootstrapAction = (
  status: FiskilSyncStatusResponse,
  apiAccountIds: string[] | null,
  vaultFiskilAccounts: VaultFiskilAccountRef[],
  options?: ResolveFiskilBootstrapOptions,
): FiskilBootstrapAction => {
  if (shouldRecoverFiskilImport(status)) {
    return "recovery";
  }

  if (options?.vaultAccountsMissingTransactions) {
    return "recovery";
  }

  if (apiAccountIds === null) {
    if (vaultFiskilAccounts.length > 0) {
      return "recovery";
    }
    return "skip";
  }

  if (apiAccountIds.length > 0) {
    const missing = fiskilApiIdsMissingFromVault(apiAccountIds, vaultFiskilAccounts);
    if (missing.length > 0) {
      return "recovery";
    }
  }

  if (vaultFiskilAccounts.length > 0) {
    return "sync";
  }

  return "skip";
};
